import React, { useMemo } from 'react';
import {
  Box,
  Chip,
  Link,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import useProfileSx from '../../hooks/useProfileSx';
import { RECORD_PERMISSION_COPY } from '../../utils/recordPermissionCopy';
import {
  RECORD_CRUD_KEYS,
  RECORD_FILE_ACTION_KEYS,
  RECORD_ROW_ACTION_KEYS,
  applyPresetToCampaign,
  clearRecordActionsForCampaign,
  getEffectiveActionsForCampaign,
  hasCrossModuleWarning,
  isCustomRecordConfig,
  upsertRecordActionsForCampaign,
} from '../../utils/recordPermissionUtils';
import { AUTHZ_FEATURES } from '../../../../constants/authz/features.constants';
import RecordActionCheckbox from './RecordActionCheckbox';
import RecordPermissionPresets from './RecordPermissionPresets';

const RECORD = AUTHZ_FEATURES.RECORDS;

export default function RecordPermissionsPanel({
  campaignId,
  campaignName,
  accountId,
  grants,
  disabled = false,
  serviceViewEnabled = true,
  onGrantsChange,
}) {
  const styles = useProfileSx();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const isCustom = useMemo(
    () => isCustomRecordConfig(grants, campaignId),
    [grants, campaignId]
  );

  const effectiveActions = useMemo(
    () => getEffectiveActionsForCampaign(grants, campaignId, accountId),
    [grants, campaignId, accountId]
  );

  const panelDisabled = disabled || !serviceViewEnabled;

  const ensureExplicitBase = () => {
    if (isCustom) {
      return grants;
    }
    return upsertRecordActionsForCampaign(grants, campaignId, { ...effectiveActions });
  };

  const handleActionChange = (codename, checked) => {
    const baseGrants = ensureExplicitBase();
    const baseEffective = getEffectiveActionsForCampaign(baseGrants, campaignId, accountId);

    let patch = { [codename]: checked };

    if (codename === RECORD.VIEW && !checked) {
      const cleared = {};
      [...RECORD_CRUD_KEYS, ...RECORD_FILE_ACTION_KEYS, ...RECORD_ROW_ACTION_KEYS].forEach(({ key }) => {
        cleared[key] = false;
      });
      patch = cleared;
    }

    if (codename === RECORD.CREATE && !checked) {
      patch[RECORD.IMPORT] = false;
    }

    if (codename === RECORD.UPDATE && !checked) {
      patch[RECORD.MOVE] = false;
    }

    if (codename === RECORD.DOWNLOAD && checked) {
      patch[RECORD.VIEW] = true;
    }

    if (codename === RECORD.IMPORT && checked) {
      patch[RECORD.VIEW] = true;
      patch[RECORD.CREATE] = true;
    }

    if (codename === RECORD.MOVE && checked) {
      patch[RECORD.VIEW] = true;
      patch[RECORD.UPDATE] = true;
    }

    if (
      codename !== RECORD.VIEW
      && [RECORD.CREATE, RECORD.UPDATE, RECORD.DELETE].includes(codename)
      && checked
      && !baseEffective[RECORD.VIEW]
    ) {
      patch[RECORD.VIEW] = true;
    }

    onGrantsChange?.(upsertRecordActionsForCampaign(baseGrants, campaignId, patch));
  };

  const handlePreset = (presetKey) => {
    onGrantsChange?.(applyPresetToCampaign(grants, campaignId, presetKey));
  };

  const handleReset = () => {
    onGrantsChange?.(clearRecordActionsForCampaign(grants, campaignId));
  };

  return (
    <Box
      role="group"
      aria-labelledby={`record-permissions-${campaignId}`}
      sx={{
        ml: { xs: 2, sm: 4 },
        mr: 1,
        my: 1,
        maxWidth: 640,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: '6px',
        p: 1.5,
        opacity: panelDisabled ? 0.5 : 1,
        pointerEvents: panelDisabled ? 'none' : 'auto',
        bgcolor: 'background.paper',
      }}
    >
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 1 }}>
        <Typography
          id={`record-permissions-${campaignId}`}
          variant="subtitle2"
          sx={{ flex: 1 }}
        >
          {RECORD_PERMISSION_COPY.panelTitle}
        </Typography>
        <Chip
          size="small"
          variant="outlined"
          color={isCustom ? 'primary' : 'default'}
          label={isCustom
            ? RECORD_PERMISSION_COPY.customBadge
            : RECORD_PERMISSION_COPY.inheritBadge}
        />
      </Box>

      {!serviceViewEnabled ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          {RECORD_PERMISSION_COPY.disabledHelper}
        </Typography>
      ) : null}

      <RecordPermissionPresets disabled={panelDisabled} onSelect={handlePreset} />

      {isCustom ? (
        <Link
          component="button"
          type="button"
          variant="caption"
          onClick={handleReset}
          sx={{ display: 'inline-block', mt: 0.75, mb: 1 }}
        >
          {RECORD_PERMISSION_COPY.resetToDefaults}
        </Link>
      ) : null}

      <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        {RECORD_PERMISSION_COPY.recordAccessSection}
      </Typography>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: { xs: 0.5, sm: 2 },
          mb: 1,
        }}
      >
        {RECORD_CRUD_KEYS.map(({ key, labelKey }) => (
          <RecordActionCheckbox
            key={key}
            label={RECORD_PERMISSION_COPY.crud[labelKey]}
            checked={effectiveActions[key]}
            disabled={
              panelDisabled
              || (key !== RECORD.VIEW && !effectiveActions[RECORD.VIEW])
            }
            ariaLabel={`${RECORD_PERMISSION_COPY.crud[labelKey]} for ${campaignName || campaignId}`}
            onChange={(checked) => handleActionChange(key, checked)}
            styles={styles}
          />
        ))}
        {RECORD_FILE_ACTION_KEYS.map(({ key, labelKey }) => (
          <RecordActionCheckbox
            key={key}
            label={RECORD_PERMISSION_COPY.crud[labelKey]}
            checked={Boolean(effectiveActions[key])}
            disabled={
              panelDisabled
              || (key === RECORD.DOWNLOAD && !effectiveActions[RECORD.VIEW])
              || (key === RECORD.IMPORT && !effectiveActions[RECORD.CREATE])
              || (key === RECORD.MOVE && !effectiveActions[RECORD.UPDATE])
            }
            ariaLabel={`${RECORD_PERMISSION_COPY.crud[labelKey]} for ${campaignName || campaignId}`}
            onChange={(checked) => handleActionChange(key, checked)}
            styles={styles}
          />
        ))}
      </Box>
      <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        {RECORD_PERMISSION_COPY.rowActionsSection}
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
          gap: 0.5,
        }}
      >
        {RECORD_ROW_ACTION_KEYS.map(({ key, labelKey }) => (
          <RecordActionCheckbox
            key={key}
            label={RECORD_PERMISSION_COPY.rowActions[labelKey]}
            checked={effectiveActions[key]}
            disabled={panelDisabled || !effectiveActions[RECORD.VIEW]}
            warningTooltip={
              hasCrossModuleWarning(grants, key)
                ? (labelKey === 'sendTemplate'
                  ? RECORD_PERMISSION_COPY.whatsappWarning
                  : RECORD_PERMISSION_COPY.interveneWarning)
                : undefined
            }
            ariaLabel={`${RECORD_PERMISSION_COPY.rowActions[labelKey]} for ${campaignName || campaignId}`}
            onChange={(checked) => handleActionChange(key, checked)}
            styles={styles}
          />
        ))}
      </Box>
    </Box>
  );
}
